import type { CalorieDayView } from '@iac/contracts';
import { useDict } from '../../i18n/dictionary';
import { api, unwrap } from '../../lib/api';
import {
  useCreateResource,
  useDeleteResource,
  useResourceQuery,
  useUpdateResource,
} from '../../lib/crud';
import { useInvalidating } from '../../lib/query';
import { caloriesDict } from './i18n';

// Coarse on purpose: an entry edit moves its day's totals and maybe the
// catalog; a meal edit reshapes every day. Every mutation invalidates them all.
const CALORIES = ['calories'] as const;

export function useDay(date: string) {
  const t = useDict(caloriesDict);
  return useResourceQuery(
    [...CALORIES, 'day', date],
    () => api.calories.getDay({ params: { date } }),
    t.loadDayFailed,
  );
}

export function useCreateEntry() {
  const t = useDict(caloriesDict);
  return useCreateResource(
    CALORIES,
    api.calories.createEntry,
    t.addEntryFailed,
  );
}

export function useUpdateEntry() {
  const t = useDict(caloriesDict);
  return useUpdateResource(
    CALORIES,
    api.calories.updateEntry,
    t.saveEntryFailed,
    t.entrySaved,
  );
}

export function useStepEntry() {
  const t = useDict(caloriesDict);
  return useInvalidating(
    CALORIES,
    async ({ id, by }: { id: string; by: 1 | -1 }) =>
      unwrap(
        await api.calories.stepEntry({ params: { id }, body: { by } }),
        200,
        t.stepFailed,
      ),
  );
}

export function useMoveEntry() {
  const t = useDict(caloriesDict);
  return useUpdateResource(
    CALORIES,
    api.calories.moveEntry,
    t.moveFailed,
    t.moved,
  );
}

export function useDeleteEntry() {
  const t = useDict(caloriesDict);
  return useDeleteResource(
    CALORIES,
    api.calories.deleteEntry,
    t.deleteEntryFailed,
  );
}

export function useProducts() {
  const t = useDict(caloriesDict);
  return useResourceQuery(
    [...CALORIES, 'products'],
    api.calories.listProducts,
    t.loadCatalogFailed,
  );
}

export function useCreateProduct() {
  const t = useDict(caloriesDict);
  return useCreateResource(
    CALORIES,
    api.calories.createProduct,
    t.addProductFailed,
    t.productAdded,
  );
}

export function useUpdateProduct() {
  const t = useDict(caloriesDict);
  return useUpdateResource(
    CALORIES,
    api.calories.updateProduct,
    t.saveProductFailed,
    t.productSaved,
  );
}

export function useDeleteProduct() {
  const t = useDict(caloriesDict);
  return useDeleteResource(
    CALORIES,
    api.calories.deleteProduct,
    t.deleteProductFailed,
  );
}

export function useLogProduct() {
  const t = useDict(caloriesDict);
  return useInvalidating(
    CALORIES,
    async ({ id, date }: { id: string; date: string }) =>
      unwrap(
        await api.calories.logProduct({ params: { id }, body: { date } }),
        201,
        t.logFailed,
      ),
  );
}

export function useDishes() {
  const t = useDict(caloriesDict);
  return useResourceQuery(
    [...CALORIES, 'dishes'],
    api.calories.listDishes,
    t.loadDishesFailed,
  );
}

export function useCreateDish() {
  const t = useDict(caloriesDict);
  return useCreateResource(
    CALORIES,
    api.calories.createDish,
    t.addDishFailed,
    t.dishAdded,
  );
}

export function useUpdateDish() {
  const t = useDict(caloriesDict);
  return useUpdateResource(
    CALORIES,
    api.calories.updateDish,
    t.saveDishFailed,
    t.dishSaved,
  );
}

export function useDeleteDish() {
  const t = useDict(caloriesDict);
  return useDeleteResource(
    CALORIES,
    api.calories.deleteDish,
    t.deleteDishFailed,
  );
}

export function useLogDish() {
  const t = useDict(caloriesDict);
  return useInvalidating(
    CALORIES,
    async ({ id, date }: { id: string; date: string }) =>
      unwrap(
        await api.calories.logDish({ params: { id }, body: { date } }),
        201,
        t.logDishFailed,
      ),
  );
}

export function useMeals() {
  const t = useDict(caloriesDict);
  return useResourceQuery(
    [...CALORIES, 'meals'],
    api.calories.listMeals,
    t.loadMealsFailed,
  );
}

export function useCreateMeal() {
  const t = useDict(caloriesDict);
  return useCreateResource(CALORIES, api.calories.createMeal, t.addMealFailed);
}

export function useRenameMeal() {
  const t = useDict(caloriesDict);
  return useUpdateResource(
    CALORIES,
    api.calories.renameMeal,
    t.renameMealFailed,
    t.mealSaved,
  );
}

export function useReorderMeals() {
  const t = useDict(caloriesDict);
  return useInvalidating(CALORIES, async (ids: string[]) =>
    unwrap(
      await api.calories.reorderMeals({ body: { ids } }),
      200,
      t.reorderFailed,
    ),
  );
}

export function useDeleteMeal() {
  const t = useDict(caloriesDict);
  return useDeleteResource(
    CALORIES,
    api.calories.deleteMeal,
    t.deleteMealFailed,
  );
}

// Its own key, so saving a weight refetches only the measurements.
const WEIGHTS = [...CALORIES, 'weights'] as const;

export function useWeights() {
  const t = useDict(caloriesDict);
  return useResourceQuery(
    WEIGHTS,
    api.calories.listWeights,
    t.loadWeightsFailed,
  );
}

/** Adds the day's weight, or replaces it if the day already has one. */
export function useSaveWeight() {
  const t = useDict(caloriesDict);
  return useInvalidating(
    WEIGHTS,
    async (body: { date: string; weightKg: string }) =>
      unwrap(await api.calories.saveWeight({ body }), 200, t.saveWeightFailed),
    t.weightSaved,
  );
}

export function useUpdateWeight() {
  const t = useDict(caloriesDict);
  return useUpdateResource(
    WEIGHTS,
    api.calories.updateWeight,
    t.saveWeightFailed,
    t.weightSaved,
  );
}

export function useDeleteWeight() {
  const t = useDict(caloriesDict);
  return useDeleteResource(
    WEIGHTS,
    api.calories.deleteWeight,
    t.deleteWeightFailed,
  );
}

// Its own key: an entry edit doesn't move the limit, and saving the limit
// changes nothing else.
const SETTINGS = [...CALORIES, 'settings'] as const;

export function useCalorieSettings() {
  const t = useDict(caloriesDict);
  return useResourceQuery(
    SETTINGS,
    api.calories.getSettings,
    t.loadSettingsFailed,
  );
}

/** Sends the target alone; `null` clears it. */
export function useUpdateTargetWeight() {
  const t = useDict(caloriesDict);
  return useInvalidating(
    SETTINGS,
    async (targetWeightKg: string | null) =>
      unwrap(
        await api.calories.updateSettings({ body: { targetWeightKg } }),
        200,
        t.saveSettingsFailed,
      ),
    t.targetSaved,
  );
}

/** Sends the limit alone; the day view is left as it was. */
export function useUpdateDailyLimit() {
  const t = useDict(caloriesDict);
  return useInvalidating(
    SETTINGS,
    async (dailyLimitKcal: string) =>
      unwrap(
        await api.calories.updateSettings({ body: { dailyLimitKcal } }),
        200,
        t.saveSettingsFailed,
      ),
    t.limitSaved,
  );
}

/** Sends the day view alone; the limit is left as it was. */
export function useUpdateDayView() {
  const t = useDict(caloriesDict);
  return useInvalidating(
    SETTINGS,
    async (dayView: CalorieDayView) =>
      unwrap(
        await api.calories.updateSettings({ body: { dayView } }),
        200,
        t.saveSettingsFailed,
      ),
    t.viewSaved,
  );
}
